import moment from "moment";
import Teachr from "../../Objects/models/Teachr";
import { capitalizeFirstLetter, validateDeposit, validatePrice } from "../../Utils/Function";
import { getActivePrestationBrandConfig, isDepositEnabled } from "../../config/environment.config";
import { sessionDescriptionOverrideError } from "../../Utils/sessionDescriptionOverride";
import { MIN_SESSIONS_MONTH, SessionRow, apiSessionsToRows, validateSessionRows } from "../DeclarationCourse/SessionList";
import { rowIssues } from "../DeclarationV2/components/planningRules";
import { DepositErrors } from "../DeclarationV2/components/DepositPanel";
import { emptyDeposit } from "../DeclarationV2/types";
import { dayLabel } from "../DeclarationV2/format";
import { DraftIssues, draftCeiling, draftHours, hourlyPaidPrice } from "../BrouillonsV2/drafts";
import { isRelationTeachrCourse, relationHourlyRate } from "../DeclarationV2/components/relationTeachr";
import { CourseEditLocationState, EditableCourse, ExistingDeposit } from "./types";

/**
 * Helpers purs de la modification d'un cours : lecture d'un cours de la
 * liste (à venir ou du mois) vers la forme éditable, contrôles avant
 * enregistrement. Copie de `validatePunctual()` (cours non brouillon) de
 * `CourseRowActions.tsx`, exprimée par champ comme pour les brouillons v2.
 */

const toNumber = (value: unknown): number => {
    const n = typeof value === "number" ? value : parseFloat(String(value ?? ""));
    return Number.isFinite(n) ? n : 0;
};

/** Séances éditables : celles de l'API pour plusieurs séances, une ligne pour un cours ponctuel (sans décalage). */
export const courseSessions = (course: any): SessionRow[] => {
    if (course?.isPunctualMonth) return apiSessionsToRows(course?.sessions, 0);
    const start = moment(course?.startDatetime);
    return [{
        key: `single-${course?.id ?? "course"}`,
        date: start.format("YYYY-MM-DD"),
        startTime: start.toISOString(),
        duration: toNumber(course?.duration) / 2,
    }];
};

const existingDepositOf = (course: any): ExistingDeposit => {
    const proofs: any[] = Array.isArray(course?.proofDeposits) ? course.proofDeposits : [];
    return {
        amount: isDepositEnabled() ? toNumber(course?.deposit) : 0,
        date: course?.depositDate ? moment(course.depositDate).format("YYYY-MM-DD") : null,
        hasAttestation: !!course?.depositAttestation,
        attestationSignedAt: course?.depositAttestation?.signedAt ?? null,
        proofIds: proofs.map((p) => p?.id).filter(Boolean),
        proofId: course?.proofDeposit?.id ?? null,
        proofUrls: [
            ...proofs.map((p) => p?.contentUrl).filter(Boolean),
            ...(course?.proofDeposit?.contentUrl ? [course.proofDeposit.contentUrl] : []),
        ],
    };
};

/** Même règle que le menu « Modifier » : transmis à l'URSSAF ou terminé, plus de modification. */
export const courseLockReason = (course: any): string | null => {
    const transmitted = course?.urssafServiceStatus != null || course?.urssafServiceStatus_id != null;
    if (transmitted) return "Transmis à l'URSSAF";
    if (course?.isOver) return `${capitalizeFirstLetter(getActivePrestationBrandConfig())} terminé${getActivePrestationBrandConfig("", "", "e")}`;
    return null;
};

/** Un cours de la liste (ligne enrichie ou état de navigation) devient un cours éditable. */
export const editableFromCourse = (course: any, context: CourseEditLocationState, teachr: Teachr | null | undefined): EditableCourse => {
    const apiSessions = Array.isArray(course?.sessions) ? course.sessions.length : 0;
    const existingDeposit = existingDepositOf(course);
    const startAt = context.startAt?.date ?? context.startAt ?? course?.startAt?.date ?? course?.startAt ?? null;
    const child = context.child ?? null;
    return {
        id: toNumber(course?.id),
        student: {
            clientId: toNumber(context.clientId ?? course?.clientId),
            childId: toNumber(context.forChildId ?? course?.forChild_id),
            firstName: course?.firstName || child?.childFirstName || course?.forChildFirstName || "",
            lastName: course?.lastName || child?.childLastName || course?.forChildLastName || "",
            addressId: course?.address_id ?? child?.addressId ?? null,
            activationDate: startAt,
            zip: course?.zip ?? child?.zip ?? null,
        },
        subjectId: course?.subjectAsked_id ?? null,
        subjectLabel: course?.subjectAsked ?? "",
        multiple: !!course?.isPunctualMonth,
        sessions: courseSessions(course),
        price: isRelationTeachrCourse(course) ? relationHourlyRate(course).toFixed(2) : hourlyPaidPrice(course?.teachrPrice, (teachr as any)?.defaultFees),
        // acompte enregistré : repris tel quel, attestation et preuve à refaire seulement s'il change
        deposit: existingDeposit.amount > 0
            ? { ...emptyDeposit(), enabled: true, amount: String(existingDeposit.amount), date: existingDeposit.date }
            : emptyDeposit(),
        sessionDescriptionOverride: course?.sessionDescriptionOverride ?? null,
        isTeachrContact: !!course?.customFees?.is_teachr_contact,
        isRelationTeachr: isRelationTeachrCourse(course),
        coursesThisRollingMonth: course?.coursesThisRollingMonth ?? null,
        coursesMonthByThisTeachr: course?.coursesMonthByThisTeachr ?? null,
        amountDeclaredThisMonth: toNumber(course?.amount),
        minSessions: course?.isPunctualMonth && apiSessions < MIN_SESSIONS_MONTH ? Math.max(1, apiSessions) : MIN_SESSIONS_MONTH,
        raw: course,
        proposalId: course?.acceptedProposal_id ?? null,
        existingDeposit,
        chapterOver: course?.chapter === "over",
        lockReason: courseLockReason(course),
    };
};

/**
 * Attestation sur l'honneur à (re)certifier : montant ou date d'acompte
 * modifiés, ou cours sans attestation (ancien régime « preuve seule »).
 * Copie de `needsDepositAttestation` de `CourseRowActions.tsx`.
 */
export const needsDepositAttestation = (course: EditableCourse): boolean => {
    const amount = course.deposit.enabled ? toNumber(course.deposit.amount) : 0;
    if (amount <= 0) return false;
    const date = course.deposit.date ? moment(course.deposit.date).format("YYYY-MM-DD") : null;
    return amount !== course.existingDeposit.amount
        || date !== course.existingDeposit.date
        || !course.existingDeposit.hasAttestation;
};

/** L'acompte a changé par rapport au cours enregistré (montant ou date). */
export const depositChanged = (course: EditableCourse): boolean => {
    const amount = course.deposit.enabled ? toNumber(course.deposit.amount) : 0;
    const date = course.deposit.date ? moment(course.deposit.date).format("YYYY-MM-DD") : null;
    return amount !== course.existingDeposit.amount || (amount > 0 && date !== course.existingDeposit.date);
};

/** Une séance déjà passée (jour strictement avant aujourd'hui) : le cours sera marqué « over », comme le formulaire existant. */
export const hasPastSession = (course: EditableCourse, now: moment.Moment = moment()): boolean => {
    const today = now.format("YYYY-MM-DD");
    return course.sessions.some((row) => !!row.date && row.date < today);
};

/**
 * Contrôles avant enregistrement, copiés de `validatePunctual()` pour un cours
 * (non brouillon) : séances (règles du planning v2 et nombre minimum), prix
 * (6 à 138 € de l'heure) et plafond mensuel, acompte (montant au plus égal à
 * ce que paie le client, date si l'acompte change, attestation et preuve
 * seulement si une nouvelle attestation est requise, les preuves déjà
 * enregistrées comptent), déroulement propre au cours. Le quota de 8 n'est
 * pas contrôlé ici : le cours modifié est déjà compté dedans.
 */
export const courseEditIssues = (course: EditableCourse, teachr: Teachr | null | undefined, now: moment.Moment = moment()): DraftIssues => {
    const hours = draftHours(course);
    const rows = rowIssues(course.sessions, { activationDate: course.student.activationDate, existing: [] });
    const sessionsCheck = course.multiple ? validateSessionRows(course.sessions, { minSessions: course.minSessions }) : { isValid: true, message: "" };
    const sessions = rows.length === 0 && !sessionsCheck.isValid ? sessionsCheck.message : null;

    const priceCheck = validatePrice(course.price, hours);
    let price: string | null = priceCheck.isValid ? null : priceCheck.message;
    const ceiling = draftCeiling(course, teachr, now);
    if (!price && ceiling.exceeds) {
        price = `Total déclaré ce mois (${ceiling.total.toFixed(0)} €) au-dessus du plafond de ${ceiling.ceiling.toFixed(0)} €. Baissez le prix ou déplacez des séances sur le mois suivant.`;
    }

    const deposit: DepositErrors = {};
    const depositAmount = course.deposit.enabled ? toNumber(course.deposit.amount) : 0;
    if (depositAmount > 0) {
        const needsAttestation = needsDepositAttestation(course);
        if (depositChanged(course)) {
            const paidByClient = hours * toNumber(course.price);
            const depositCheck = validateDeposit(depositAmount, paidByClient, true);
            if (!depositCheck.isValid) deposit.amount = depositCheck.message;
            if (!course.deposit.date) deposit.date = "Indiquez la date de réception de l'acompte.";
        }
        if (needsAttestation) {
            if (!course.deposit.attestationAccepted) deposit.attestation = "Vous avez modifié l'acompte, certifiez l'attestation sur l'honneur pour enregistrer.";
            const hasProof = !!course.deposit.proof || course.existingDeposit.proofIds.length > 0 || course.existingDeposit.proofId != null;
            if (!hasProof) deposit.proof = "Joignez une preuve d'acompte (PDF, PNG ou JPEG) pour enregistrer.";
        }
    }

    const descriptionError = sessionDescriptionOverrideError(course.sessionDescriptionOverride);
    const description = descriptionError === "" ? null : descriptionError;

    const all = [
        ...rows.map((issue) => {
            const row = course.sessions.find((r) => r.key === issue.key);
            const label = row?.date ? dayLabel(row.date).toLowerCase() : `la séance ${course.sessions.findIndex((r) => r.key === issue.key) + 1}`;
            return `${label.charAt(0).toUpperCase()}${label.slice(1)} : ${issue.message}`;
        }),
        sessions,
        price,
        deposit.amount,
        deposit.date,
        deposit.attestation,
        deposit.proof,
        description,
    ].filter((m): m is string => !!m);

    return { rows, sessions, price, deposit, description, quota: null, all };
};
